import { useEffect, useState } from "react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  DollarSign, Users2, TrendingUp, X, Copy, Check, Sparkles, Mail, Phone, MapPin,
  Briefcase, Globe2, Send, ChevronRight,
} from "lucide-react";

const statuses = ["applicant", "under_review", "approved", "active", "paused", "inactive", "declined"];

export default function AdminGlobalLink() {
  const [list, setList] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (statusFilter) p.set("status", statusFilter);
    const [l, m] = await Promise.all([
      api.get(`/referral-partners?${p}`),
      api.get("/referral-partners/metrics/summary"),
    ]);
    setList(l.data);
    setMetrics(m.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const update = async (id, patch) => {
    try { await api.patch(`/referral-partners/${id}`, patch); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="page-admin-global-link">
      <AdminHeader breadcrumb="Relationships" title="Global Link Studio" subtitle="TGL affiliate applications, questionnaire review, activation & commission tracking." />
      <div className="p-8 space-y-6">
        {/* Metric tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile label="Total partners" value={metrics.total ?? 0} icon={Users2} testid="gl-stat-total" />
          <MetricTile label="Active" value={metrics.active ?? 0} icon={Sparkles} testid="gl-stat-active"
            hint={`${metrics.applicants ?? 0} pending review`} />
          <MetricTile label="Leads referred" value={metrics.leads_referred ?? 0} icon={TrendingUp} testid="gl-stat-referrals"
            hint={`${metrics.closed_deals ?? 0} closed`} />
          <MetricTile label="Commissions due" value={`$${(metrics.commission_due ?? 0).toLocaleString()}`}
            icon={DollarSign} testid="gl-stat-commission"
            hint={`$${(metrics.commission_paid ?? 0).toLocaleString()} paid`} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2" data-testid="gl-status-filters">
          <button onClick={() => setStatusFilter("")}
                  className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest ${!statusFilter ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso"}`}
                  data-testid="gl-filter-all">All</button>
          {statuses.map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
                    className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest ${statusFilter === s ? "bg-altura-espresso text-altura-ivory" : "border border-altura-espresso/15 text-altura-espresso hover:border-altura-gold"}`}
                    data-testid={`gl-filter-${s}`}>{s.replace("_", " ")}</button>
          ))}
        </div>

        {/* Table */}
        <div className="admin-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-[0.15em] text-altura-charcoal/50">
              <tr className="border-b border-altura-espresso/10">
                <th className="text-left py-3 px-2">Name</th>
                <th className="text-left py-3 px-2">Email</th>
                <th className="text-left py-3 px-2">Residence</th>
                <th className="text-left py-3 px-2">Profession</th>
                <th className="text-left py-3 px-2">Status</th>
                <th className="text-left py-3 px-2">Referral Code</th>
                <th className="text-right py-3 px-2">Referred</th>
                <th className="text-right py-3 px-2">Comm. Due</th>
                <th className="text-right py-3 px-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id} className="border-b border-altura-espresso/5 hover:bg-altura-beige/20" data-testid={`gl-row-${r.id}`}>
                  <td className="py-3 px-2 font-medium text-altura-espresso">{r.name}</td>
                  <td className="py-3 px-2 text-altura-charcoal/75">{r.email}</td>
                  <td className="py-3 px-2 text-altura-charcoal/75">{r.country || r.questionnaire?.residence || "—"}</td>
                  <td className="py-3 px-2 text-altura-charcoal/75">{r.partner_type || r.questionnaire?.profession || "—"}</td>
                  <td className="py-3 px-2">
                    <select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })}
                            className="admin-select w-36 text-xs" data-testid={`gl-status-${r.id}`}>
                      {statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-2 font-mono text-xs">
                    {r.referral_code ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-altura-gold/10 px-2 py-1 text-altura-gold-dark border border-altura-gold/30">
                        {r.referral_code}
                      </span>
                    ) : (
                      <span className="text-altura-charcoal/40">—</span>
                    )}
                  </td>
                  <td className="py-3 px-2 text-right font-mono text-xs">{r.leads_referred || 0}</td>
                  <td className="py-3 px-2 text-right font-mono text-xs">${(r.commission_due || 0).toLocaleString()}</td>
                  <td className="py-3 px-2 text-right pr-4">
                    <button onClick={() => setOpenId(r.id)}
                            className="inline-flex items-center gap-1 text-xs text-altura-espresso hover:text-altura-gold-dark transition"
                            data-testid={`gl-view-${r.id}`}>
                      Review <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr><td colSpan="9" className="py-14 text-center text-altura-charcoal/50">
                  No referral partners yet. Applications submitted through <code>/global-link</code> will appear here.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openId && <PartnerDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function MetricTile({ label, value, icon: Icon, testid, hint }) {
  return (
    <div className="admin-stat" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/50">{label}</div>
        <Icon className="h-4 w-4 text-altura-gold-dark" />
      </div>
      <div className="font-editorial text-3xl text-altura-espresso">{value}</div>
      {hint && <div className="text-[11px] text-altura-charcoal/50">{hint}</div>}
    </div>
  );
}

function PartnerDrawer({ id, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/referral-partners/${id}`);
    setP(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/referral-partners/${id}/generate-code`);
      toast.success(`Referral code assigned: ${data.referral_code}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Failed to generate code");
    } finally { setBusy(false); }
  };

  const setStatus = async (s) => {
    try {
      await api.patch(`/referral-partners/${id}`, { status: s });
      toast.success(`Status → ${s.replace("_", " ")}`);
      await load();
      onChanged?.();
    } catch { toast.error("Failed"); }
  };

  const copyCode = async () => {
    if (!p?.referral_code) return;
    try {
      await navigator.clipboard.writeText(p.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {
      // Clipboard API can fail in restricted contexts — silently ignore.
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="gl-partner-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <aside className="relative z-50 h-full w-full max-w-2xl overflow-y-auto bg-altura-ivory shadow-2xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-altura-espresso/10 bg-altura-ivory px-8 py-5">
          <div>
            <div className="eyebrow text-altura-gold-dark">Global Link Applicant</div>
            <h2 className="mt-1 font-editorial text-2xl text-altura-espresso">
              {p?.name || "Loading…"}
            </h2>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-altura-espresso/5" data-testid="gl-drawer-close">
            <X className="h-4 w-4 text-altura-charcoal/70" />
          </button>
        </header>

        {!p ? (
          <div className="p-8 text-altura-charcoal/50">Loading partner profile…</div>
        ) : (
          <div className="p-8 space-y-8">
            {/* Contact + Actions */}
            <section className="rounded-lg border border-altura-espresso/10 bg-white p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <IconRow icon={Mail} label="Email" value={p.email} />
                <IconRow icon={Phone} label="Phone" value={p.phone} />
                <IconRow icon={MapPin} label="Residence" value={p.country || p.questionnaire?.residence} />
                <IconRow icon={Briefcase} label="Profession" value={p.partner_type || p.questionnaire?.profession} />
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-altura-espresso/10 pt-6">
                <StatusPill status={p.status} />
                {p.referral_code ? (
                  <button onClick={copyCode}
                          className="inline-flex items-center gap-2 rounded-full border border-altura-gold/40 bg-altura-gold/10 px-4 py-1.5 text-xs font-mono text-altura-gold-dark hover:bg-altura-gold/20 transition"
                          data-testid="gl-copy-code">
                    {p.referral_code}
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  <button onClick={generate} disabled={busy}
                          className="btn-gold text-xs px-4 py-1.5 disabled:opacity-60"
                          data-testid="gl-generate-code">
                    {busy ? "Generating…" : "Generate referral code"} <Sparkles className="h-3.5 w-3.5" />
                  </button>
                )}
                {p.status !== "active" && p.referral_code && (
                  <button onClick={() => setStatus("active")} className="btn-ghost-espresso text-xs px-4 py-1.5" data-testid="gl-activate">
                    Activate partner
                  </button>
                )}
                {p.status !== "declined" && (
                  <button onClick={() => setStatus("declined")} className="text-xs text-red-700 hover:text-red-900 transition" data-testid="gl-decline">
                    Decline
                  </button>
                )}
              </div>
            </section>

            {/* Full questionnaire */}
            {p.questionnaire && <QuestionnaireCard q={p.questionnaire} />}

            {/* Free-text notes */}
            {p.notes && (
              <section className="rounded-lg border border-altura-espresso/10 bg-white p-6">
                <div className="eyebrow text-altura-gold-dark">Composed lead summary</div>
                <pre className="mt-4 whitespace-pre-wrap font-serif text-sm text-altura-charcoal/85 leading-relaxed">{p.notes}</pre>
              </section>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function IconRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-altura-gold-dark mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">{label}</div>
        <div className="mt-0.5 text-altura-espresso">{value}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    applicant: "bg-blue-50 text-blue-700 border-blue-200",
    under_review: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    active: "bg-altura-gold/10 text-altura-gold-dark border-altura-gold/40",
    paused: "bg-neutral-50 text-neutral-600 border-neutral-200",
    inactive: "bg-neutral-50 text-neutral-500 border-neutral-200",
    declined: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest ${colors[status] || "border-altura-espresso/15"}`}>
      {status?.replace("_", " ")}
    </span>
  );
}

function QuestionnaireCard({ q }) {
  const rows = [
    { label: "Preferred contact", value: q.preferred_contact },
    { label: "Social / Web", value: q.social_media },
    { label: "In related industry", value: q.in_related_industry?.toUpperCase() },
    { label: "Network size", value: q.network_size },
    { label: "Regions", value: (q.regions || []).join(", ") },
    { label: "Sharing methods", value: (q.sharing_methods || []).join(", ") },
    { label: "Prior affiliate", value: q.prior_affiliate?.toUpperCase() },
    { label: "Time commitment", value: q.time_commitment },
    { label: "Preferred payout", value: (q.payout_methods || []).join(", ") },
    { label: "Understands commission", value: q.understands_commission?.toUpperCase() },
    { label: "Agrees to T&C", value: q.agrees_to_tc?.toUpperCase() },
  ].filter((r) => r.value);

  return (
    <section className="rounded-lg border border-altura-espresso/10 bg-white p-6" data-testid="gl-questionnaire">
      <div className="flex items-center gap-2 mb-5">
        <Send className="h-4 w-4 text-altura-gold-dark" />
        <div className="eyebrow text-altura-gold-dark">The Global Link Questionnaire</div>
      </div>

      {q.why_join && (
        <div className="mb-6 rounded-md bg-altura-beige/30 p-5 border border-altura-espresso/5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50 mb-2">Why they want to join</div>
          <p className="text-altura-espresso italic leading-relaxed">&ldquo;{q.why_join}&rdquo;</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
        {rows.map((r) => (
          <div key={r.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-altura-charcoal/50">{r.label}</div>
            <div className="mt-0.5 text-sm text-altura-espresso">{r.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
